<template>
  <section class="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-hero">
    <!-- Floating Healthcare Icons -->
    <div
      v-for="(icon, index) in floatingIcons"
      :key="index"
      class="absolute floating-icon"
      :style="{ left: icon.x, top: icon.y }"
      v-motion
      :initial="{ opacity: 0, scale: 0 }"
      :enter="{ 
        opacity: 0.3, 
        scale: 1,
        y: [0, -20, 0],
        x: [0, 10, 0]
      }"
      :duration="6000"
      :delay="icon.delay * 1000"
    >
      <component :is="icon.component" :size="40" class="text-light-purple" />
    </div>

    <!-- Main Content -->
    <div class="container mx-auto px-6 text-center relative z-10">
      <div
        v-motion
        :initial="{ opacity: 0, y: 50 }"
        :enter="{ opacity: 1, y: 0 }"
        :duration="800"
        :delay="200"
      >
        <!-- Brand (not H1 — primary topic is the headline below) -->
        <div
          v-motion
          :initial="{ scale: 0.8 }"
          :enter="{ scale: 1 }"
          :duration="600"
          :delay="400"
        >
          <p class="text-6xl md:text-8xl font-poppins font-bold text-light-purple mb-4">
            Homvisit
          </p>
          <div class="w-32 h-1 bg-gradient-to-r from-light-purple to-transparent mx-auto rounded-full"></div>
        </div>

        <!-- Single H1: primary page topic -->
        <h1
          class="text-4xl md:text-6xl font-poppins font-bold text-dark-gray mb-6 leading-tight"
          v-motion
          :initial="{ opacity: 0, y: 30 }"
          :enter="{ opacity: 1, y: 0 }"
          :duration="800"
          :delay="600"
        >
          Corporate Health Camps
          <br />
          <span class="text-light-purple">& Phlebotomist Teams in Kerala</span>
        </h1>

        <!-- Subtext -->
        <p
          class="text-lg md:text-xl text-dark-gray/80 mb-10 max-w-3xl mx-auto leading-relaxed"
          v-motion
          :initial="{ opacity: 0, y: 30 }"
          :enter="{ opacity: 1, y: 0 }"
          :duration="800"
          :delay="800"
        >
          Onsite phlebotomy for companies, labs, and employee checkup camps across all districts.
          Home blood collection available as a secondary service.
        </p>

        <!-- CTA Buttons -->
        <div
          class="flex flex-col md:flex-row gap-4 justify-center items-center"
          v-motion
          :initial="{ opacity: 0, y: 30 }"
          :enter="{ opacity: 1, y: 0 }"
          :duration="800"
          :delay="1000"
        >
          <button
            class="btn-neumorphic text-light-purple hover:text-white hover:bg-light-purple group relative overflow-hidden"
            @mouseenter="onButtonHover"
            @mouseleave="onButtonLeave"
            @click="openServiceModal('corporate-camp')"
          >
            <span class="relative z-10">Book Corporate Camp</span>
            <div
              class="absolute inset-0 bg-light-purple transition-all duration-300 opacity-0 group-hover:opacity-100"
              style="border-radius: 24px"
            />
          </button>

          <RouterLink
            to="/jobs"
            class="btn-neumorphic text-dark-gray hover:text-light-purple group"
            @mouseenter="onButtonHover"
            @mouseleave="onButtonLeave"
          >
            Phlebos: Join Network
          </RouterLink>

          <button
            class="text-sm font-medium text-dark-gray/60 hover:text-light-purple underline underline-offset-4"
            @click="openServiceModal('home-collection')"
          >
            Need home collection?
          </button>
        </div>
      </div>
    </div>

    <!-- Scroll Indicator -->
    <div
      class="absolute bottom-8 left-1/2 transform -translate-x-1/2"
      v-motion
      :initial="{ opacity: 0 }"
      :enter="{ opacity: 1 }"
      :delay="2000"
    >
      <div
        class="w-6 h-10 border-2 border-light-purple rounded-full flex justify-center"
        v-motion
        :enter="{ y: [0, 10, 0] }"
        :duration="2000"
        :repeat="Infinity"
      >
        <div
          class="w-1 h-3 bg-light-purple rounded-full mt-2"
          v-motion
          :enter="{ y: [0, 12, 0] }"
          :duration="2000"
          :repeat="Infinity"
        />
      </div>
    </div>
  </section>
</template>

<script>
import { Heart, Stethoscope, Droplets } from 'lucide-vue-next'

export default {
  name: 'HeroSection',
  components: {
    Heart,
    Stethoscope,
    Droplets
  },
  setup() {
    const floatingIcons = [
      { component: Heart, delay: 0, x: '10%', y: '20%' },
      { component: Stethoscope, delay: 2, x: '80%', y: '30%' },
      { component: Droplets, delay: 4, x: '15%', y: '70%' },
      { component: Heart, delay: 1, x: '85%', y: '75%' },
      { component: Stethoscope, delay: 3, x: '50%', y: '15%' },
    ]

    const onButtonHover = (e) => {
      e.target.style.transform = 'scale(1.05) translateY(-2px)'
    }

    const onButtonLeave = (e) => {
      e.target.style.transform = 'scale(1) translateY(0px)'
    }

    const openServiceModal = (serviceType) => {
      // Emit event to parent component to open modal
      window.dispatchEvent(new CustomEvent('openServiceModal', { detail: serviceType }))
    }

    return {
      floatingIcons,
      onButtonHover,
      onButtonLeave,
      openServiceModal
    }
  }
}
</script>
